import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../components/ui/Toast";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { 
  Database,
  Lightbulb, 
  Users, 
  Workflow, 
  Eye, 
  Trash2, 
  Loader2, 
  MessageSquare, 
  Plus, 
  Search,
  X,
  Grid3X3,
  List,
  Settings,
  Star,
  Clock,
  Tag,
  RefreshCw
} from 'lucide-react';
import { useAdminApi } from '../apis/admin-apis';
import { useToolsApi } from '../apis/tools-apis';
import { useAgentsApi } from '../apis/agents-apis';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { usePromptsApi } from '../apis/prompts-apis';
import { AdminData } from '../types';
import { ConfirmationDialog } from '../components/ConfirmationDialog';

type ItemType = 'tool' | 'agent' | 'playbook' | 'prompt';
type FilterType = 'all' | 'tool' | 'agent' | 'playbook' | 'prompt';
type SortType = 'name' | 'type' | 'created' | 'updated';
type ViewMode = 'grid' | 'list';

interface CoreItem {
  id: string;
  name: string;
  description: string;
  type: ItemType;
  tags?: string[];
  createdAt: string;
  updatedAt?: string;
  isCore?: boolean;
}

const formatItemType = (type: ItemType | undefined): string => {
  if (!type) return 'Item';
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const getItemIcon = (type: ItemType) => {
  switch (type) {
    case 'tool': return Lightbulb;
    case 'agent': return Users;
    case 'playbook': return Workflow;
    case 'prompt': return MessageSquare;
    default: return Database;
  }
};

const getItemColor = (type: ItemType) => {
  switch (type) {
    case 'tool': return 'from-yellow-500 to-yellow-600';
    case 'agent': return 'from-blue-500 to-blue-600';
    case 'playbook': return 'from-purple-500 to-purple-600';
    case 'prompt': return 'from-green-500 to-green-600';
    default: return 'from-gray-500 to-gray-600';
  }
};

const CoreItemsPage: React.FC = () => {
  const adminApi = useAdminApi();
  const toolsApi = useToolsApi();
  const agentsApi = useAgentsApi();
  const playbooksApi = usePlaybooksApi();
  const promptsApi = usePromptsApi();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortType, setSortType] = useState<SortType>('name');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<CoreItem | null>(null);
  
  const { addToast } = useToast();

  const normalizeItems = (data: AdminData | null): CoreItem[] => {
    if (!data) return [];
    
    const allItems: CoreItem[] = [];
    
    // Tools
    data.tools?.forEach(tool => {
      allItems.push({
        id: tool.toolId || '',
        name: tool.toolName,
        description: tool.toolDescription || '',
        type: 'tool',
        tags: tool.toolTags || [],
        createdAt: new Date().toISOString(), // Default to current date if not available
        updatedAt: new Date().toISOString(),
        isCore: tool.isCore
      });
    });

    // Agents
    data.agents?.forEach(agent => {
      allItems.push({
        id: agent.agentId,
        name: agent.agentName,
        description: agent.agentDescription || '',
        type: 'agent',
        tags: agent.agentTags || [],
        createdAt: new Date().toISOString(), // Default to current date if not available
        updatedAt: new Date().toISOString(),
        isCore: agent.isCore
      });
    });

    // Playbooks
    data.playbooks?.forEach(playbook => {
      allItems.push({
        id: playbook.playbookId,
        name: playbook.playbookName,
        description: playbook.playbookDescription || '',
        type: 'playbook',
        tags: playbook.playbookTags || [],
        createdAt: new Date().toISOString(), // Default to current date if not available
        updatedAt: new Date().toISOString()
      });
    });

    // Prompts
    data.prompts?.forEach(prompt => {
      allItems.push({
        id: prompt.promptId,
        name: prompt.promptName,
        description: prompt.promptInformation || '', // Note: promptInformation, not promptDescription
        type: 'prompt',
        tags: prompt.promptTags || [],
        createdAt: new Date().toISOString(), // Default to current date if not available
        updatedAt: new Date().toISOString()
      });
    });

    return allItems;
  };

  const fetchAdminData = async () => {
    const isInitialLoad = loading;
    if (!isInitialLoad) setRefreshing(true);

    try {
      const response = await adminApi.getAdminData();
      setAdminData(response);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      addToast({
        title: "Error",
        description: "Failed to load administrative data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  // Filter and sort items
  const getFilteredAndSortedItems = (): CoreItem[] => {
    let items = normalizeItems(adminData);

    // Apply search filter
    if (searchTerm) {
      items = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      items = items.filter(item => item.type === filterType);
    }

    // Apply sorting
    switch (sortType) {
      case 'name':
        items.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'type':
        items.sort((a, b) => a.type.localeCompare(b.type));
        break;
      case 'created':
        items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'updated':
        items.sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime());
        break;
    }

    return items;
  };

  const filteredItems = getFilteredAndSortedItems();

  // Stats calculations
  const stats = useMemo(() => {
    const items = normalizeItems(adminData);
    return {
      total: items.length,
      tools: items.filter(item => item.type === 'tool').length,
      agents: items.filter(item => item.type === 'agent').length,
      playbooks: items.filter(item => item.type === 'playbook').length,
      prompts: items.filter(item => item.type === 'prompt').length,
      core: items.filter(item => item.isCore).length
    };
  }, [adminData]);

  const handleView = (item: CoreItem) => {
    if (item.type === 'tool') {
      navigate(`/studio/tools/edit/${item.id}`);
    } else {
      navigate(`/studio/${item.type}s/edit/${item.id}`);
    }
  };

  const handleDelete = (item: CoreItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const getDeleteFunction = (type: ItemType) => {
    switch (type) {
      case 'tool': return toolsApi.deleteTool;
      case 'agent': return agentsApi.deleteAgent;
      case 'playbook': return playbooksApi.deletePlaybook;
      case 'prompt': return promptsApi.deletePrompt;
      default: throw new Error(`Unknown type: ${type}`);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!itemToDelete) return;

    try {
      setIsDeletingId(itemToDelete.id);
      const deleteFunction = getDeleteFunction(itemToDelete.type);
      await deleteFunction(itemToDelete.id);
      
      addToast({
        title: "Success",
        description: `${formatItemType(itemToDelete.type)} has been deleted.`,
        variant: "default",
      });

      await fetchAdminData();
    } catch (error) {
      console.error(`Error deleting ${itemToDelete.type}:`, error);
      addToast({
        title: "Error",
        description: `Failed to delete ${itemToDelete.type}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderItemCard = (item: CoreItem) => {
    const ItemIcon = getItemIcon(item.type);
    const gradientColor = getItemColor(item.type);

    return (
      <Card key={item.id} className="group hover:shadow-xl transition-all duration-300 border-purple-200 hover:border-purple-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-purple-200/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            {/* Left side - Icon and Info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Item Icon */}
              <div className={`bg-gradient-to-br ${gradientColor} p-3 rounded-xl shadow-sm flex-shrink-0`}>
                <ItemIcon className="h-6 w-6 text-white" />
              </div>
              
              {/* Item Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors truncate tracking-tight">
                    {item.name}
                  </h3>
                  <Badge variant="outline" className="text-xs capitalize">
                    {item.type}
                  </Badge>
                  {item.isCore && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-sm flex-shrink-0">
                      <Star className="w-3 h-3 mr-1" />
                      Core
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {item.description || 'No description provided.'}
                </p>

                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {item.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{item.tags.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleView(item)}
                className="h-8 w-8 p-0 hover:bg-purple-50 hover:text-purple-600"
                title="View Item"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(item)}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                disabled={isDeletingId === item.id}
                title="Delete Item"
              >
                {isDeletingId === item.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Bottom section - Stats */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3 text-blue-500" />
                Created {formatDate(item.createdAt)}
              </span>
              {item.updatedAt && (
                <span className="flex items-center gap-1 font-medium">
                  <Settings className="w-3 h-3 text-emerald-500" />
                  Updated {formatDate(item.updatedAt)}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderListItem = (item: CoreItem) => {
    const ItemIcon = getItemIcon(item.type);
    const gradientColor = getItemColor(item.type);

    return (
      <Card key={item.id} className="mb-3 hover:shadow-lg transition-all duration-200 hover:border-blue-300 group">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Item Icon */}
              <div className={`bg-gradient-to-br ${gradientColor} p-2 rounded-lg flex-shrink-0`}>
                <ItemIcon className="h-5 w-5 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                    {item.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">
                      {item.type}
                    </Badge>
                    {item.isCore && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-sm">
                        <Star className="w-3 h-3 mr-1" />
                        Core
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {item.description || 'No description provided.'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Created {formatDate(item.createdAt)}
                  {item.updatedAt && ` • Updated ${formatDate(item.updatedAt)}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleView(item)}
                className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
              >
                <Eye className="w-4 h-4 mr-2" />
                View
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(item)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                disabled={isDeletingId === item.id}
              >
                {isDeletingId === item.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="w-8 h-8" />
          <p className="text-gray-600">Loading core items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/30 via-transparent to-purple-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Database className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Core Items
                  </h1>
                  <p className="text-indigo-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Manage your essential platform components including tools, agents, playbooks, and prompts from one centralized location
                  </p>
                </div>
              </div>
              
              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">{stats.total}</span> Total Items
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-yellow-200">{stats.tools}</span> Tools
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-blue-200">{stats.agents}</span> Agents
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-purple-200">{stats.playbooks}</span> Playbooks
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={fetchAdminData}
                disabled={refreshing}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-4 py-3 rounded-2xl font-semibold"
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search core items"
              className="pl-10 pr-4 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FilterType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="all">All Items</option>
            <option value="tool">Tools</option>
            <option value="agent">Agents</option>
            <option value="playbook">Playbooks</option>
            <option value="prompt">Prompts</option>
          </select>

          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as SortType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="name">Sort by Name</option>
            <option value="type">Sort by Type</option>
            <option value="created">Sort by Created</option>
            <option value="updated">Sort by Updated</option>
          </select>

          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
          {searchTerm || filterType !== 'all' ? (
            <>Showing {filteredItems.length} of {stats.total} items</>
          ) : (
            <>Showing all {stats.total} items</>
          )}
        </p>
      </div>

      {/* Items Grid/List */}
      {filteredItems.length === 0 ? (
        <Card className="p-12 text-center border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 p-4 rounded-full">
              <Database className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 tracking-tight">
                {searchTerm ? 'No items found' : 'No core items yet'}
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                {searchTerm 
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Get started by creating your first core item for the platform.'
                }
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid gap-4 grid-cols-1 lg:grid-cols-2' 
            : 'space-y-3'
        }>
          {filteredItems.map(item => 
            viewMode === 'grid' ? renderItemCard(item) : renderListItem(item)
          )}
        </div>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title={`Delete ${formatItemType(itemToDelete?.type)}`}
        description={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
      />
    </div>
  );
};

export default CoreItemsPage;